import { useMemo, useState } from 'react';
import { usePushSubscription } from '../push/usePushSubscription';
import { detectPlatform, isStandalone } from '../push/platform';
import { useInstallPrompt } from './useInstallPrompt';

/**
 * "Install me" flow (DESIGN-BRIEF §4-A3): platform-aware Add-to-Home-Screen instructions,
 * then a notification-permission priming screen that explains the value *before* the
 * system permission prompt fires.
 */
export function InstallOnboarding(): JSX.Element {
  const platform = useMemo(() => detectPlatform(), []);
  const standalone = useMemo(() => isStandalone(), []);
  const [primed, setPrimed] = useState(false);

  return (
    <div className="mx-auto max-w-lg space-y-6 px-4 py-8">
      <header>
        <h1 className="font-display text-2xl tracking-tight text-fg">Install AwwYeah</h1>
        <p className="mt-1 text-fg-2">
          Add it to your home screen for a full-screen app, offline access, and push notifications
          when something changes.
        </p>
      </header>

      {standalone ? (
        <InstalledCard />
      ) : (
        <>
          <NativeInstallButton />
          <InstallSteps platform={platform} />
        </>
      )}

      <section className="space-y-3 rounded-lg border border-line p-4">
        <h2 className="text-lg font-semibold text-fg">Turn on notifications</h2>
        <p className="text-sm text-fg-2">
          AwwYeah can nudge you when someone adds to a shared list, changes a plan you're part of,
          or a reminder comes due — never anything else. You can turn this off any time in Settings.
        </p>
        {!primed ? (
          <button
            type="button"
            onClick={() => setPrimed(true)}
            className="min-h-[44px] rounded-xl bg-inverse px-4 py-2 font-semibold text-inverse-fg"
          >
            Sounds good — continue
          </button>
        ) : (
          <NotificationPrompt />
        )}
      </section>
    </div>
  );
}

/**
 * Chrome/Edge hand us a deferrable native install prompt; if so, offer the one-tap path
 * above the manual steps. If the user dismissed the native dialog earlier, this button is
 * how they get it back (until the browser stops offering the event for the session).
 */
function NativeInstallButton(): JSX.Element | null {
  const { canPromptNatively, promptNatively } = useInstallPrompt();
  if (!canPromptNatively) return null;

  return (
    <button
      type="button"
      onClick={() => void promptNatively()}
      data-testid="native-install-button"
      className="min-h-[44px] w-full rounded-xl bg-inverse px-4 py-2 font-semibold text-inverse-fg"
    >
      Install now
    </button>
  );
}

function InstalledCard(): JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-surface p-4 text-sync-ok">
      <p className="font-medium">You're already installed — nice.</p>
      <p className="mt-1 text-sm">AwwYeah is running full-screen from your home screen.</p>
    </div>
  );
}

function InstallSteps({ platform }: { platform: ReturnType<typeof detectPlatform> }): JSX.Element {
  if (platform === 'ios-safari') {
    return (
      <ol className="list-decimal space-y-3 rounded-lg border border-line p-4 pl-8 text-sm text-fg">
        <li>
          Tap the <span className="font-semibold">Share</span> icon (the square with an arrow) in
          Safari's toolbar.
        </li>
        <li>
          Scroll down and tap <span className="font-semibold">Add to Home Screen</span>.
        </li>
        <li>
          Tap <span className="font-semibold">Add</span> in the top-right corner.
        </li>
      </ol>
    );
  }

  if (platform === 'android-chrome') {
    return (
      <ol className="list-decimal space-y-3 rounded-lg border border-line p-4 pl-8 text-sm text-fg">
        <li>
          Tap the <span className="font-semibold">⋮</span> menu in Chrome's toolbar.
        </li>
        <li>
          Tap <span className="font-semibold">Add to Home screen</span> (or{' '}
          <span className="font-semibold">Install app</span> if Chrome offers it directly).
        </li>
        <li>Confirm by tapping Add / Install.</li>
      </ol>
    );
  }

  if (platform === 'desktop') {
    return (
      <ol className="list-decimal space-y-3 rounded-lg border border-line p-4 pl-8 text-sm text-fg">
        <li>
          Click the install icon in the address bar (or open the browser menu and choose{' '}
          <span className="font-semibold">Install AwwYeah…</span>).
        </li>
        <li>Confirm the install prompt.</li>
      </ol>
    );
  }

  return (
    <div className="rounded-lg border border-line p-4 text-sm text-fg-2">
      Open this page in Safari (iPhone/iPad) or Chrome (Android/desktop) to install AwwYeah to your
      home screen.
    </div>
  );
}

function NotificationPrompt(): JSX.Element {
  const { permission, subscribed, busy, error, subscribe } = usePushSubscription();

  if (permission === 'granted' && subscribed) {
    return <p className="text-sm font-medium text-sync-ok">Notifications are on.</p>;
  }

  if (permission === 'denied') {
    return (
      <p className="text-sm text-fg-2">
        Notifications are blocked in your browser settings. You can re-enable them there any time.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => void subscribe()}
        disabled={busy}
        className="min-h-[44px] rounded-xl bg-inverse px-4 py-2 font-semibold text-inverse-fg disabled:opacity-60"
      >
        {busy ? 'Requesting…' : 'Enable notifications'}
      </button>
      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}
